// The six selectable fishermen: same rig (see player.ts's buildFisherman),
// varied by color/build/beard, each with a distinct gameplay tradeoff.
// Ids are mirrored (as plain strings) on the server in rooms.ts so it can
// enforce one-per-player in multiplayer without knowing what an avatar is.

export interface AvatarVisual {
  kimonoColor: number;
  sashColor: number;
  hatColor: number;
  beardColor: number;
  beardScale: number; // 0 = clean-shaven
  heightScale: number;
  buildScale: number;
}

export interface AvatarStats {
  speedMult: number;
  restDrainMult: number; // higher = tires faster
  biteMod: number; // added to restBiteMod before it reaches computeBiteRadius()
}

export interface AvatarDef {
  id: string;
  name: string;
  blurb: string;
  visual: AvatarVisual;
  stats: AvatarStats;
}

export const AVATARS: AvatarDef[] = [
  {
    id: 'old-genji',
    name: 'Old Genji',
    blurb: 'A lifetime on the water sharpened his eye for a bite — but the long days wear him down faster now.',
    visual: { kimonoColor: 0x2b3a67, sashColor: 0x8a7550, hatColor: 0xd9b36a, beardColor: 0xd8d5cc, beardScale: 1, heightScale: 1, buildScale: 1.05 },
    stats: { speedMult: 1, restDrainMult: 1.25, biteMod: 0.12 },
  },
  {
    id: 'ume',
    name: 'Ume',
    blurb: 'Steady, patient hands that rarely miss a bite — though she\'s in no hurry to get anywhere.',
    visual: { kimonoColor: 0x6b3540, sashColor: 0x5a2a2a, hatColor: 0xd9b36a, beardColor: 0xd8d5cc, beardScale: 0.8, heightScale: 0.95, buildScale: 0.95 },
    stats: { speedMult: 0.88, restDrainMult: 1, biteMod: 0.08 },
  },
  {
    id: 'taro',
    name: 'Taro',
    blurb: 'Young and lanky, quick on his feet — but the fish can tell he\'s still learning.',
    visual: { kimonoColor: 0x3f7d4f, sashColor: 0x8a7550, hatColor: 0xd9b36a, beardColor: 0xd8d5cc, beardScale: 0, heightScale: 1.08, buildScale: 0.85 },
    stats: { speedMult: 1.15, restDrainMult: 1, biteMod: -0.1 },
  },
  {
    id: 'kenji',
    name: 'Kenji',
    blurb: 'Built stocky and hardy — he can go all day without tiring, if he can just settle down and wait for a bite.',
    visual: { kimonoColor: 0x46607a, sashColor: 0x5a4a3a, hatColor: 0xd9b36a, beardColor: 0xd8d5cc, beardScale: 0, heightScale: 0.95, buildScale: 1.15 },
    stats: { speedMult: 1, restDrainMult: 0.8, biteMod: -0.08 },
  },
  {
    id: 'hana',
    name: 'Hana',
    blurb: 'Light-footed and quick across the hexes — but all that energy burns her rest down fast.',
    visual: { kimonoColor: 0x7a4a68, sashColor: 0x8a7550, hatColor: 0xd9b36a, beardColor: 0xd8d5cc, beardScale: 0, heightScale: 0.97, buildScale: 0.85 },
    stats: { speedMult: 1.12, restDrainMult: 1.15, biteMod: 0 },
  },
  {
    id: 'sachi',
    name: 'Sachi',
    blurb: 'Calm and unhurried — she barely tires, though she\'s never in a rush to get there.',
    visual: { kimonoColor: 0x2f6f6a, sashColor: 0x8a7550, hatColor: 0xd9b36a, beardColor: 0xd8d5cc, beardScale: 0, heightScale: 1, buildScale: 1 },
    stats: { speedMult: 0.9, restDrainMult: 0.85, biteMod: 0 },
  },
];

export let activeAvatarId = AVATARS[0].id;

export function setActiveAvatar(id: string): void {
  activeAvatarId = id;
}

export function avatarById(id: string | undefined): AvatarDef {
  return AVATARS.find((a) => a.id === id) ?? AVATARS[0];
}

export function getActiveAvatar(): AvatarDef {
  return avatarById(activeAvatarId);
}
